/**
 * F1 Data Hooks
 *
 * React Query hooks for fetching F1 data from the oRPC API.
 * These hooks provide caching, loading states, and error handling.
 *
 * Query Options:
 * - Exported queryOptions functions can be used in route loaders for SSR prefetching
 * - Use with `context.queryClient.ensureQueryData(queryOptions)` in loaders
 * - Components use hooks which internally use the same queryOptions
 */

import { queryOptions, useQuery } from "@tanstack/react-query";
import { orpc } from "@/orpc/client";

// ============================================================================
// Types
// ============================================================================

export interface Season {
  id: string;
  year: number;
}

export interface Race {
  id: string;
  round: number;
  raceId: number;
  name: string;
  slug: string;
  date: string | null;
  winnerName: string | null;
  winnerTeam: string | null;
  laps: number | null;
  raceTime: string | null;
  isSprintWeekend: boolean;
  hasFantasyData: boolean;
}

export interface Driver {
  id: string;
  slug: string;
  firstName: string;
  lastName: string;
  fullName: string;
  code: string | null;
  nationality: string | null;
  permanentNumber: number | null;
}

export interface Constructor {
  id: string;
  name: string;
  fullName: string | null;
  slug: string;
  code?: string | null;
}

// Flattened driver standing interface for SSR streaming compatibility
// (nested objects cause Seroval serialization errors during streaming)
export interface DriverStanding {
  id: string;
  position: number;
  points: number;
  wins: number;
  podiums: number;
  // Flattened driver fields
  driverSlug: string;
  driverFirstName: string;
  driverLastName: string;
  driverFullName: string;
  driverCode: string | null;
  driverNationality: string | null;
  driverPermanentNumber: number | null;
  // Flattened constructor fields
  constructorName: string;
  constructorSlug: string;
}

// Flattened constructor standing interface for SSR streaming compatibility
// (nested objects cause Seroval serialization errors during streaming)
export interface ConstructorStanding {
  id: string;
  position: number;
  points: number;
  wins: number;
  podiums: number;
  // Flattened constructor fields
  constructorName: string;
  constructorFullName: string | null;
  constructorSlug: string;
}

export interface DriverFantasyResult {
  id: string;
  code: string;
  price: number;
  totalPoints: number;
  qualifyingPosition: number | null;
  racePosition: number | null;
  raceDnf: boolean;
  positionsGained: number;
  overtakes: number;
  fastestLap: boolean;
  driverOfTheDay: boolean;
  sprintPosition: number | null;
  sprintDnf: boolean;
  sprintPositionsGained: number;
  sprintOvertakes: number;
  sprintFastestLap: boolean;
  driver?: Driver;
  constructor?: Constructor;
}

export interface ConstructorFantasyResult {
  id: string;
  price: number;
  totalPoints: number;
  // These fields exist in the database but may not be populated
  qualifyingPosition1: number | null;
  qualifyingPosition2: number | null;
  racePosition1: number | null;
  racePosition2: number | null;
  raceDnf1: boolean;
  raceDnf2: boolean;
  positionsGained: number;
  overtakes: number;
  fastestLap: boolean;
  // Additional fields from the database
  dnfCount: number;
  overallFastestPitStop: boolean;
  worldRecordPitStop: boolean;
  pitStopTimePoints: number; // Pit stop time-based points (already includes fastest-pitstop bonus)
  teamworkPoints: number | null; // Teamwork bonus from API (null = not yet seeded)
  // DQ penalty points (applied to constructor, not driver)
  qualifyingDqPoints: number; // -5 per driver DQ in qualifying
  raceDqPoints: number; // -10 per driver DQ in race
  sprintDqPoints: number; // -10 per driver DQ in sprint
  // Sprint fields
  sprintPosition1: number | null;
  sprintPosition2: number | null;
  sprintDnf1: boolean;
  sprintDnf2: boolean;
  sprintPositionsGained: number;
  sprintOvertakes: number;
  sprintFastestLap: boolean;
  sprintDnfCount: number;
  constructor?: Constructor;
}

// ============================================================================
// Query Options (for SSR prefetching in route loaders)
// ============================================================================

/**
 * Query options for fetching all seasons
 * Use in route loaders: context.queryClient.ensureQueryData(seasonsQueryOptions())
 */
export const seasonsQueryOptions = () =>
  queryOptions({
    queryKey: ["f1", "seasons"] as const,
    queryFn: () => orpc.f1.getSeasons.call({}),
    staleTime: 1000 * 60 * 60, // 1 hour - seasons don't change often
  });

/**
 * Query options for fetching driver standings
 * Use in route loaders: context.queryClient.ensureQueryData(driverStandingsQueryOptions(2025))
 */
export const driverStandingsQueryOptions = (season: number) =>
  queryOptions({
    queryKey: ["f1", "driverStandings", season, "final"] as const,
    queryFn: () => orpc.f1.getDriverStandings.call({ season }),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

/**
 * Query options for fetching constructor standings
 * Use in route loaders: context.queryClient.ensureQueryData(constructorStandingsQueryOptions(2025))
 */
export const constructorStandingsQueryOptions = (season: number) =>
  queryOptions({
    queryKey: ["f1", "constructorStandings", season, "final"] as const,
    queryFn: () => orpc.f1.getConstructorStandings.call({ season }),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

/**
 * Query options for fetching races by season
 * Use in route loaders: context.queryClient.ensureQueryData(racesQueryOptions(2025))
 */
export const racesQueryOptions = (season: number) =>
  queryOptions({
    queryKey: ["f1", "races", season] as const,
    queryFn: () => orpc.f1.getRaces.call({ season }),
    staleTime: 1000 * 60 * 60, // 1 hour
  });

/**
 * Query options for fetching race stages
 * Use in route loaders: context.queryClient.ensureQueryData(raceStagesQueryOptions(2025, 'abu-dhabi'))
 */
export const raceStagesQueryOptions = (season: number, raceSlug: string) =>
  queryOptions({
    queryKey: ["f1", "raceStages", season, raceSlug] as const,
    queryFn: () => orpc.f1.getRaceStages.call({ season, raceSlug }),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

/**
 * Query options for fetching budget builder data
 * Use in route loaders: context.queryClient.ensureQueryData(budgetBuilderQueryOptions(2025, 'abu-dhabi', 'after_race'))
 */
export const budgetBuilderQueryOptions = (
  season: number,
  raceSlug: string,
  stage: BudgetBuilderStage,
) =>
  queryOptions({
    queryKey: ["f1", "budgetBuilderData", season, raceSlug, stage] as const,
    queryFn: () =>
      orpc.f1.getBudgetBuilderData.call({ season, raceSlug, stage }),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

/**
 * Query options for fetching driver fantasy results
 * Use in route loaders: context.queryClient.prefetchQuery(driverFantasyResultsQueryOptions(2025, 'abu-dhabi'))
 */
export const driverFantasyResultsQueryOptions = (
  season: number,
  raceSlug: string,
) =>
  queryOptions({
    queryKey: ["f1", "driverFantasyResults", season, raceSlug] as const,
    queryFn: () => orpc.f1.getDriverFantasyResults.call({ season, raceSlug }),
    staleTime: 1000 * 60 * 60, // 1 hour - historical data doesn't change
  });

/**
 * Query options for fetching constructor fantasy results
 * Use in route loaders: context.queryClient.prefetchQuery(constructorFantasyResultsQueryOptions(2025, 'abu-dhabi'))
 */
export const constructorFantasyResultsQueryOptions = (
  season: number,
  raceSlug: string,
) =>
  queryOptions({
    queryKey: ["f1", "constructorFantasyResults", season, raceSlug] as const,
    queryFn: () =>
      orpc.f1.getConstructorFantasyResults.call({ season, raceSlug }),
    staleTime: 1000 * 60 * 60, // 1 hour - historical data doesn't change
  });

// ============================================================================
// Hooks
// ============================================================================

/**
 * Fetch all available seasons
 * Uses seasonsQueryOptions for consistent cache keys with SSR
 */
export function useSeasons() {
  return useQuery(seasonsQueryOptions());
}

/**
 * Fetch all races for a specific season
 * Uses racesQueryOptions for consistent cache keys
 */
export function useRaces(season: number | undefined) {
  return useQuery({
    ...racesQueryOptions(season ?? 0),
    enabled: !!season,
  });
}

/**
 * Fetch a single race by season and slug
 */
export function useRace(season: number | undefined, slug: string | undefined) {
  return useQuery({
    queryKey: ["f1", "race", season, slug],
    queryFn: () => orpc.f1.getRace.call({ season: season!, slug: slug! }),
    enabled: !!season && !!slug,
    staleTime: 1000 * 60 * 60,
  });
}

/**
 * Fetch driver standings for a season
 * Uses driverStandingsQueryOptions for consistent cache keys with SSR
 * Note: raceRound parameter is kept for backwards compatibility but currently unused
 * (we only have end-of-season standings data)
 */
export function useDriverStandings(
  season: number | undefined,
  _raceRound?: number | null,
) {
  return useQuery({
    ...driverStandingsQueryOptions(season ?? 0),
    enabled: !!season,
  });
}

/**
 * Fetch constructor standings for a season
 * Uses constructorStandingsQueryOptions for consistent cache keys with SSR
 * Note: raceRound parameter is kept for backwards compatibility but currently unused
 * (we only have end-of-season standings data)
 */
export function useConstructorStandings(
  season: number | undefined,
  _raceRound?: number | null,
) {
  return useQuery({
    ...constructorStandingsQueryOptions(season ?? 0),
    enabled: !!season,
  });
}

/**
 * Fetch all drivers, optionally filtered by season
 */
export function useDrivers(season?: number) {
  return useQuery({
    queryKey: ["f1", "drivers", season ?? "all"],
    queryFn: () => orpc.f1.getDrivers.call({ season }),
    staleTime: 1000 * 60 * 60, // 1 hour
  });
}

/**
 * Fetch all constructors, optionally filtered by season
 */
export function useConstructors(season?: number) {
  return useQuery({
    queryKey: ["f1", "constructors", season ?? "all"],
    queryFn: () => orpc.f1.getConstructors.call({ season }),
    staleTime: 1000 * 60 * 60, // 1 hour
  });
}

/**
 * Fetch race results for a specific race and session type
 */
export function useRaceResults(
  season: number | undefined,
  raceSlug: string | undefined,
  sessionType = "race",
) {
  return useQuery({
    queryKey: ["f1", "raceResults", season, raceSlug, sessionType],
    queryFn: () =>
      orpc.f1.getRaceResults.call({
        season: season!,
        raceSlug: raceSlug!,
        sessionType,
      }),
    enabled: !!season && !!raceSlug,
    staleTime: 1000 * 60 * 60, // 1 hour
  });
}

/**
 * Fetch driver statistics
 */
export function useDriverStats(driverSlug: string, season?: number) {
  return useQuery({
    queryKey: ["f1", "driverStats", driverSlug, season ?? "all"],
    queryFn: () => orpc.f1.getDriverStats.call({ driverSlug, season }),
    enabled: !!driverSlug,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

// ============================================================================
// Utility Hooks
// ============================================================================

/**
 * Get the current/latest season year
 */
export function useCurrentSeason() {
  const { data: seasons } = useSeasons();
  // Seasons are ordered by year DESC, so first one is the latest
  return seasons?.[0]?.year ?? new Date().getFullYear();
}

/**
 * Get the next upcoming race for a season
 */
export function useNextRace(season: number | undefined) {
  const { data: races, ...rest } = useRaces(season);

  const nextRace = races?.find((race) => {
    if (!race.date) return false;
    const raceDate = new Date(race.date);
    return raceDate > new Date();
  });

  return {
    ...rest,
    data: nextRace ?? null,
  };
}

// ============================================================================
// Fantasy Results Hooks
// ============================================================================

/**
 * Fetch driver fantasy results for a specific race
 * Returns detailed fantasy scoring data including positions, overtakes, etc.
 * Uses driverFantasyResultsQueryOptions for consistent cache keys with SSR
 */
export function useDriverFantasyResults(
  season: number | undefined,
  raceSlug: string | undefined,
) {
  return useQuery({
    ...driverFantasyResultsQueryOptions(season ?? 0, raceSlug ?? ""),
    enabled: !!season && !!raceSlug,
  });
}

/**
 * Fetch constructor fantasy results for a specific race
 * Returns detailed fantasy scoring data for constructors
 * Uses constructorFantasyResultsQueryOptions for consistent cache keys with SSR
 */
export function useConstructorFantasyResults(
  season: number | undefined,
  raceSlug: string | undefined,
) {
  return useQuery({
    ...constructorFantasyResultsQueryOptions(season ?? 0, raceSlug ?? ""),
    enabled: !!season && !!raceSlug,
  });
}

// ============================================================================
// Budget Builder Hooks
// ============================================================================

// Stage type for budget builder
export type BudgetBuilderStage =
  | "pre_weekend"
  | "after_fp1"
  | "after_fp2"
  | "after_fp3"
  | "after_sprint_quali"
  | "after_sprint"
  | "after_quali"
  | "after_race";

export interface BudgetBuilderDriver {
  driverId: string;
  driverCode: string;
  driverNumber: number | null; // Driver's car number (e.g., 1 for VER, 44 for HAM)
  driverName: string;
  constructorName: string;
  constructorSlug: string;
  constructorColor: string;
  currentPrice: number;
  // Historical points for prediction
  pointsRMinus2: number | null;
  pointsRMinus1: number | null;
  // Historical prices for PPM calculation
  priceRMinus2: number | null;
  priceRMinus1: number | null;
  tier: "A" | "B";
  // New fields for completed races
  currentRacePoints?: number | null;
  actualPriceChange?: number | null;
  actualPriceChangeBucket?: string | null;
  isCompleted?: boolean;
}

export interface BudgetBuilderConstructor {
  constructorId: string;
  constructorCode: string;
  constructorName: string;
  constructorSlug: string;
  constructorColor: string;
  currentPrice: number;
  // Historical points for prediction
  pointsRMinus2: number | null;
  pointsRMinus1: number | null;
  // Historical prices for PPM calculation
  priceRMinus2: number | null;
  priceRMinus1: number | null;
  tier: "A" | "B";
  // New fields for completed races
  currentRacePoints?: number | null;
  actualPriceChange?: number | null;
  actualPriceChangeBucket?: string | null;
  isCompleted?: boolean;
}

export interface BudgetBuilderRaceContext {
  currentRace: { round: number; name: string; slug: string } | null;
  rMinus1Race: { round: number; name: string; slug: string } | null;
  rMinus2Race: { round: number; name: string; slug: string } | null;
}

// Session position data for live predictions
export interface SessionPositionData {
  driverCode: string;
  position: number | null;
}

// Session data for all sessions
export interface BudgetBuilderSessionData {
  fp1: SessionPositionData[];
  fp2: SessionPositionData[];
  fp3: SessionPositionData[];
  sprintQuali: SessionPositionData[];
  sprint: SessionPositionData[];
  qualifying: SessionPositionData[];
  race: SessionPositionData[];
}

export interface BudgetBuilderData {
  drivers: BudgetBuilderDriver[];
  constructors: BudgetBuilderConstructor[];
  raceContext: BudgetBuilderRaceContext;
  // Session data for live predictions
  sessionData?: BudgetBuilderSessionData;
  isSprintWeekend?: boolean;
  stage?: BudgetBuilderStage;
  earlySeasonMode?: boolean;
}

/**
 * Fetch budget builder data for a specific race and stage
 * Returns driver and constructor fantasy points for the last 2 races (R-2, R-1)
 * plus session-specific data for the selected stage to adjust predictions
 * Uses budgetBuilderQueryOptions for consistent cache keys with SSR
 */
export function useBudgetBuilderData(
  season: number | undefined,
  raceSlug: string | undefined,
  stage: BudgetBuilderStage = "pre_weekend",
) {
  return useQuery({
    ...budgetBuilderQueryOptions(season ?? 0, raceSlug ?? "", stage),
    enabled: !!season && !!raceSlug,
  });
}

// ============================================================================
// Race Stages Hooks
// ============================================================================

export interface RaceStage {
  id: string;
  name: string;
  description: string;
  order: number;
  isAvailable: boolean;
  hasData: boolean;
}

export interface RaceStagesData {
  isSprintWeekend: boolean;
  stages: RaceStage[];
  currentStage: string | null;
}

// ============================================================================
// Team Calculator Types (with raw race history for client-side predictions)
// ============================================================================

/**
 * Raw historical data for a single driver's race performance.
 * Used by client-side algorithms to compute predictions.
 */
export interface DriverRaceHistory {
  season: number; // Which season this race was from (e.g. 2026)
  raceRound: number;
  raceSlug: string;
  raceName: string;
  isSprintWeekend: boolean;
  // Core scoring data
  totalPoints: number;
  price: number; // Needed for PPM calculations
  // Performance details (for advanced algorithms)
  qualifyingPosition: number | null;
  racePosition: number | null;
  raceDnf: boolean;
  positionsGained: number;
  overtakes: number;
  fastestLap: boolean;
  driverOfTheDay: boolean;
  // Sprint data
  sprintPosition: number | null;
  sprintDnf: boolean;
  sprintPositionsGained: number;
  sprintOvertakes: number;
  sprintFastestLap: boolean;
}

/**
 * Raw historical data for a single constructor's race performance.
 */
export interface ConstructorRaceHistory {
  season: number; // Which season this race was from (e.g. 2026)
  raceRound: number;
  raceSlug: string;
  raceName: string;
  isSprintWeekend: boolean;
  // Core scoring data
  totalPoints: number;
  price: number;
  // Performance details
  fastestLap: boolean;
  dnfCount: number;
  positionsGained: number;
  overtakes: number;
  pitStopTimePoints: number;
  // Sprint data
  sprintFastestLap: boolean;
  sprintDnfCount: number;
  sprintPositionsGained: number;
  sprintOvertakes: number;
}

/**
 * Driver data with raw race history for client-side predictions.
 */
export interface TeamCalculatorDriver {
  id: string;
  driverSlug: string; // Canonical slug e.g. "lando-norris" - used for all joins and matching
  code: string; // 3-letter code like "VER" - for display
  permanentNumber: number | null; // Driver number like 1, 44
  fullName: string;
  constructorSlug: string;
  constructorName: string;
  currentPrice: number;
  totalSeasonPoints: number;
  // Raw race history (last 10 races) for client-side algorithm
  raceHistory: DriverRaceHistory[];
  // Live session positions (populated based on current stage)
  fp1Position?: number | null;
  fp2Position?: number | null;
  fp3Position?: number | null;
  sprintQualiPosition?: number | null;
  sprintPosition?: number | null;
  qualiPosition?: number | null;
  // Actual results for completed races (used at after_race stage)
  currentRacePoints?: number | null;
  actualPriceChange?: number | null;
  isCompleted?: boolean;
}

/**
 * Constructor data with raw race history for client-side predictions.
 */
export interface TeamCalculatorConstructor {
  id: string;
  name: string;
  slug: string;
  code: string;
  currentPrice: number;
  totalSeasonPoints: number;
  // Raw race history (last 10 races) for client-side algorithm
  raceHistory: ConstructorRaceHistory[];
  // Actual results for completed races (used at after_race stage)
  currentRacePoints?: number | null;
  actualPriceChange?: number | null;
  isCompleted?: boolean;
}

export interface TeamCalculatorRaceContext {
  season: number; // Current season year (e.g. 2026)
  raceName: string | null;
  raceRound: number;
  totalRacesInSeason: number;
  isSprintWeekend: boolean;
  currentStage: BudgetBuilderStage;
}

export interface TeamCalculatorData {
  drivers: TeamCalculatorDriver[];
  constructors: TeamCalculatorConstructor[];
  raceContext: TeamCalculatorRaceContext;
  earlySeasonMode?: boolean;
  /** Budget slider bounds from full-season prices (see computeBudgetRange). */
  budgetMin?: number;
  budgetMax?: number;
}

/**
 * Fetch available stages for a specific race
 * Returns all stages with their availability status based on completed sessions
 * Uses raceStagesQueryOptions for consistent cache keys with SSR
 */
export function useRaceStages(
  season: number | undefined,
  raceSlug: string | undefined,
) {
  return useQuery({
    ...raceStagesQueryOptions(season ?? 0, raceSlug ?? ""),
    enabled: !!season && !!raceSlug,
  });
}

// ============================================================================
// Team Calculator Query Options and Hook
// ============================================================================

/**
 * Query options for fetching team calculator data
 * Use in route loaders: context.queryClient.ensureQueryData(teamCalculatorQueryOptions(2025, 'abu-dhabi', 'after_race'))
 */
export const teamCalculatorQueryOptions = (
  season: number,
  raceSlug: string,
  stage: BudgetBuilderStage,
) =>
  queryOptions({
    queryKey: ["f1", "teamCalculatorData", season, raceSlug, stage] as const,
    queryFn: () =>
      orpc.f1.getTeamCalculatorData.call({ season, raceSlug, stage }),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

/**
 * Fetch team calculator data for a specific race and stage
 * Returns drivers and constructors with prices, season points, form average, and predictions
 * Uses teamCalculatorQueryOptions for consistent cache keys with SSR
 */
export function useTeamCalculatorData(
  season: number | undefined,
  raceSlug: string | undefined,
  stage: BudgetBuilderStage = "pre_weekend",
) {
  return useQuery({
    ...teamCalculatorQueryOptions(season ?? 0, raceSlug ?? "", stage),
    enabled: !!season && !!raceSlug,
  });
}
